import { ForwardedRef, forwardRef, useImperativeHandle } from 'react';
import { ScrollView, View } from 'react-native';
import Animated, {
  FadeInDown,
  FadeOutUp,
  SlideOutRight,
} from 'react-native-reanimated';
import { SlideInRight } from 'react-native-reanimated';
import {
  OverlayProvider,
  useOverlay,
} from '@/common/components/Overlay/Overlay.context';
import {
  TOverlayChildren,
  TOverlayProps,
  TOverlayRef,
} from '@/common/components/Overlay/Overlay.types';
import { useOnKeyPress } from '@/common/components/Overlay/Overlay.utils';
import { OverlayBackdrop } from '@/common/components/Overlay/OverlayBackdrop/OverlayBackdrop';
import { OverlayChildren } from '@/common/components/Overlay/OverlayChildren/OverlayChildren';
import { OverlayHeader } from '@/common/components/Overlay/OverlayHeader/OverlayHeader';
import { OverlayPortal } from '@/common/components/Overlay/OverlayPortal/OverlayPortal';
import { Stack } from '@/common/components/Stack/Stack';
import { styles } from './OverlayCenter.styles';

export const OverlayCenter = forwardRef(
  (
    {
      header,
      footer,
      children,
      onClose,
      size = 'sm',
      closeOnOutsideClick = true,
      horizontalPosition = 'right',
      height = 'auto',
      className,
      backgroundColor,
      name = 'overlay',
      hostName = 'overlay',
      overflow = 'hidden',
      backgroundComponent,
      scrollable = true,
    }: TOverlayProps,
    ref: ForwardedRef<TOverlayRef>
  ) => {
    useOnKeyPress((evt) => {
      if (evt.key === 'Escape') onClose();
    }, closeOnOutsideClick);

    useImperativeHandle(ref, () => ({
      close: onClose,
    }));

    const enterAnimation =
      horizontalPosition === 'center' ? FadeInDown : SlideInRight;
    const exitAnimation =
      horizontalPosition === 'center' ? FadeOutUp : SlideOutRight;

    return (
      <OverlayPortal name={name} hostName={hostName}>
        <Stack
          justify="center"
          className={styles.wrapper({ position: horizontalPosition })}
        >
          <OverlayBackdrop
            onDismiss={closeOnOutsideClick ? onClose : undefined}
            animated
          />
          <OverlayProvider onClose={onClose}>
            <Animated.View
              className={styles.container({
                size,
                height,
                backgroundColor,
                className,
                overflow,
              })}
              entering={enterAnimation}
              exiting={exitAnimation}
            >
              {backgroundComponent && (
                <View className={styles.backgroundComponent}>
                  {backgroundComponent}
                </View>
              )}
              {header && <OverlayHeader>{header}</OverlayHeader>}
              <OverlayCenterContent scrollable={scrollable}>
                {children}
              </OverlayCenterContent>
              <OverlayChildren>{footer}</OverlayChildren>
            </Animated.View>
          </OverlayProvider>
        </Stack>
      </OverlayPortal>
    );
  }
);
OverlayCenter.displayName = 'OverlayCenter';

export function OverlayCenterContent({
  children,
  scrollable = true,
}: {
  children?: TOverlayChildren;
  scrollable?: boolean;
}) {
  const { onScroll } = useOverlay();

  if (!scrollable) {
    return (
      <View className={styles.content}>
        <OverlayChildren>{children}</OverlayChildren>
      </View>
    );
  }

  return (
    <ScrollView
      className={styles.content}
      contentContainerClassName={styles.scrollContent}
      scrollEventThrottle={100}
      onScroll={onScroll}
    >
      <OverlayChildren>{children}</OverlayChildren>
    </ScrollView>
  );
}
